import {
  FilterDivider,
  SectionTitle,
} from 'components/Explore/Lessons/LessonFiltersModal/LessonFilters';
import TabSwitcher from 'components/TabSwitcher';
import FilterBox from 'components/forms/FilterBox/FilterBox';
import { SliderNumberRange } from 'components/forms/Slider';
import Switch from 'components/forms/Switch/Switch';
import { TournamentFiltersProps } from './props';
import {
  COMPETITION_FORMATS,
  COMPETITION_GENDER_MENU,
  MAXIMUM_AGE,
  MAXIMUM_COST,
  MINIMUM_AGE,
  MINIMUM_COST,
  TEAM_TYPES,
  TOURNAMENT_DATES,
  TOURNAMENT_DISTANCE_IMPERIAL_OPTIONS,
  TOURNAMENT_TYPE_OPTIONS,
} from './utils';

const TournamentFilters: React.FC<TournamentFiltersProps> = ({
  selectedDate,
  setSelectedDate,
  isPrizeMoney,
  setIsPrizeMoney,
  costMinimum,
  setCostMinimum,
  costMaximum,
  setCostMaximum,
  ageMinimum,
  setAgeMinimum,
  ageMaximum,
  setAgeMaximum,
  teamType,
  setTeamType,
  competitionFormat,
  setCompetitionFormat,
  competitionGender,
  setCompetitionGender,
  distance,
  setDistance,
  selectedType,
  setSelectedType,
}) => {
  return (
    <div className="flex flex-auto flex-col gap-ds-xl overflow-y-auto px-4 py-6 md:px-ds-3xl">
      {/* The type switch and the distance chips move up into the header at `xl` and
          `md` respectively (see ExploreTournaments.tsx), so each hides at exactly the
          width its header counterpart appears. Dividers render only when the section
          above them is also on screen. */}
      <div className="xl:hidden">
        <TabSwitcher
          options={TOURNAMENT_TYPE_OPTIONS}
          selectedValue={selectedType}
          onValueChange={setSelectedType}
          keepIconColor
        />
      </div>
      <FilterDivider className="md:hidden" />
      <div className="md:hidden">
        <SectionTitle>Distance</SectionTitle>
        <div className="grid grid-cols-3 gap-4">
          {TOURNAMENT_DISTANCE_IMPERIAL_OPTIONS.map((newDistance) => (
            <FilterBox
              key={newDistance.id}
              label={newDistance.name}
              onClick={() => setDistance(newDistance)}
              isSelected={distance.id === newDistance.id}
            />
          ))}
        </div>
      </div>
      <FilterDivider className="xl:hidden" />
      <div>
        <SectionTitle>Format</SectionTitle>
        <div className="grid grid-cols-3 gap-4">
          {COMPETITION_FORMATS.map((newCompetitionType) => {
            return (
              <FilterBox
                key={newCompetitionType.id}
                label={newCompetitionType.name}
                onClick={() =>
                  setCompetitionFormat(
                    newCompetitionType.id === competitionFormat ? null : newCompetitionType.id,
                  )
                }
                isSelected={newCompetitionType.id === competitionFormat}
              />
            );
          })}
        </div>
      </div>
      <FilterDivider />
      <div>
        <SectionTitle>Type</SectionTitle>
        <div className="grid grid-cols-3 gap-4 max-sm:grid-cols-2">
          {TEAM_TYPES.map((newTeamType) => {
            return (
              <FilterBox
                key={newTeamType.id}
                label={newTeamType.name}
                onClick={() => setTeamType(newTeamType.id === teamType ? null : newTeamType.id)}
                isSelected={newTeamType.id === teamType}
              />
            );
          })}
        </div>
      </div>
      <FilterDivider />
      {/* Like Format and Type above, there is no explicit "Any" chip — clicking the
          selected chip clears back to no filter. */}
      <div>
        <SectionTitle>Gender</SectionTitle>
        <div className="grid grid-cols-3 gap-4">
          {COMPETITION_GENDER_MENU.map((gender) => (
            <FilterBox
              key={gender.id}
              label={gender.name}
              onClick={() =>
                setCompetitionGender(gender.id === competitionGender ? null : gender.id)
              }
              isSelected={gender.id === competitionGender}
            />
          ))}
        </div>
      </div>
      <FilterDivider />
      <div>
        <SectionTitle>Date</SectionTitle>
        <div className="grid grid-cols-3 gap-4">
          {TOURNAMENT_DATES.map((date) => (
            <FilterBox
              key={date.id}
              label={date.name}
              onClick={() => setSelectedDate(date.id === selectedDate ? null : date.id)}
              isSelected={date.id === selectedDate}
            />
          ))}
        </div>
      </div>
      <FilterDivider />
      <div>
        <SectionTitle>Total fee</SectionTitle>
        <div className="pt-10">
          <SliderNumberRange
            rangeMinimum={MINIMUM_COST}
            rangeMaximum={MAXIMUM_COST}
            valueMinimum={costMinimum}
            valueMaximum={costMaximum}
            setValueMinumum={(newMinimum) => setCostMinimum(newMinimum)}
            setValueMaximum={(newMaximum) => setCostMaximum(newMaximum)}
            step={5}
            decimals={0}
            prefix="$"
            isMaximumOpenEnded
          />
        </div>
      </div>
      <FilterDivider />
      <div>
        <SectionTitle>Age</SectionTitle>
        <div className="pt-10">
          <SliderNumberRange
            rangeMinimum={MINIMUM_AGE}
            rangeMaximum={MAXIMUM_AGE}
            valueMinimum={ageMinimum}
            valueMaximum={ageMaximum}
            setValueMinumum={(newMinimum) => setAgeMinimum(newMinimum)}
            setValueMaximum={(newMaximum) => setAgeMaximum(newMaximum)}
            step={1}
            decimals={0}
          />
        </div>
      </div>
      <FilterDivider />
      <div className="flex-start flex justify-between gap-4">
        <SectionTitle>Prize money only</SectionTitle>
        <Switch isActive={isPrizeMoney} toggleIsActive={() => setIsPrizeMoney(!isPrizeMoney)} />
      </div>
    </div>
  );
};

export default TournamentFilters;
